// Neural Template Type Definitions

export interface TemplateElement {
  id: string;
  type: 'question' | 'statement' | 'checklist';
  content: string;
  order: number;
  category: 'opening' | 'discovery' | 'presentation' | 'closing' | 'follow-up';
  required?: boolean;
  placeholder?: string;
}

export interface TemplateMetadata {
  estimatedDuration?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  industry?: string;
  tags?: string[];
}

export interface Template {
  id: string;
  name: string;
  description?: string;
  voicePrompt?: string; // Original voice input
  elements: TemplateElement[];
  category: string;
  metadata: TemplateMetadata;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateFilters {
  search: string;
  category: string;
  difficulty: string;
  tags: string[];
}

export interface TemplatePagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

export interface TemplateListState {
  templates: Template[];
  loading: boolean;
  error: string | null;
  filters: TemplateFilters;
  pagination: TemplatePagination;
}

export interface TemplateEditorState {
  selectedTemplate: Template | null;
  isEditing: boolean;
  isDirty: boolean;
  isGenerating: boolean;
  generationError: string | null;
}

export interface VoiceGenerationRequest {
  voicePrompt: string;
  context?: {
    industry?: string;
    meetingType?: string;
    clientType?: string;
    duration?: string;
  };
}

export interface VoiceGenerationResponse {
  template: Omit<Template, 'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'isActive'>;
  confidence: number;
  suggestions?: string[];
}

export interface TemplateUsage {
  id: string;
  templateId: string;
  transcriptId?: string;
  usedAt: string;
  completionRate: number;
  sessionDuration?: number;
}

// Component Props Interfaces
export interface TemplateListProps {
  templates: Template[];
  selectedTemplate: Template | null;
  loading: boolean;
  error: string | null;
  filters: TemplateFilters;
  onSelectTemplate: (template: Template) => void;
  onCreateNew: () => void;
  onFiltersChange: (filters: Partial<TemplateFilters>) => void;
  onLoadMore: () => void;
  hasMore: boolean;
}

export interface TemplateEditorProps {
  template: Template | null;
  isEditing: boolean;
  isDirty: boolean;
  onSave: (template: Template) => Promise<void>;
  onCancel: () => void;
  onDelete: (templateId: string) => Promise<void>;
  onElementAdd: (element: Omit<TemplateElement, 'id'>) => void;
  onElementUpdate: (elementId: string, updates: Partial<TemplateElement>) => void;
  onElementDelete: (elementId: string) => void;
  onElementReorder: (elementId: string, newOrder: number) => void;
}

export interface VoiceTemplateGeneratorProps {
  onTemplateGenerated: (template: Omit<Template, 'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'isActive'>) => void;
  isGenerating: boolean;
  generationError: string | null;
  onClearError: () => void;
}

export interface TemplateElementProps {
  element: TemplateElement;
  isEditing: boolean;
  onUpdate: (updates: Partial<TemplateElement>) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

// API Response Types
export interface TemplateListResponse {
  templates: Template[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface TemplateResponse {
  template: Template;
}

export interface TemplateCreateRequest {
  name: string;
  description?: string;
  voicePrompt?: string;
  elements: Omit<TemplateElement, 'id'>[];
  category: Template['category'];
  metadata: TemplateMetadata;
}

export interface TemplateUpdateRequest extends Partial<TemplateCreateRequest> {
  id: string;
}

// Error Types
export interface TemplateError {
  code: string;
  message: string;
  details?: Record<string, any>;
}

// Constants
export const TEMPLATE_CATEGORIES = [
  'general',
  'consultation', 
  'discovery',
  'presentation',
  'follow-up',
  'compliance'
] as const;

export const ELEMENT_TYPES = [
  'question',
  'statement', 
  'checklist'
] as const;

export const ELEMENT_CATEGORIES = [
  'opening',
  'discovery',
  'presentation', 
  'closing',
  'follow-up'
] as const;

export const DIFFICULTY_LEVELS = [
  'beginner',
  'intermediate',
  'advanced'
] as const;

// Display mapping for difficulty levels
export const DIFFICULTY_LABELS: Record<string, string> = {
  beginner: 'Standard',
  intermediate: 'Intermediate',
  advanced: 'Advanced'
}; 